import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Truck, Smartphone, ArrowRight, Sparkles, CheckCircle2, TrendingUp, Mail, Lock, Eye, EyeOff, LogOut, ShieldCheck, AlertCircle } from 'lucide-react';
const PORTAL_AUTH_KEY = 'pair2.portal-auth';
const ALLOWED_USERS: Record<string, {
  name: string;
  password: string;
}> = {
  'fadi@easylease.ae': {
    name: 'Fadi',
    password: 'Sheel@0123'
  },
  'bashar@sheel.ae': {
    name: 'Bashar',
    password: 'Sheel@0123'
  }
};
type PortalUser = {
  email: string;
  name: string;
};
function loadPortalUser(): PortalUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PORTAL_AUTH_KEY);
    return raw ? JSON.parse(raw) as PortalUser : null;
  } catch {
    return null;
  }
}
function savePortalUser(user: PortalUser | null) {
  try {
    if (user) sessionStorage.setItem(PORTAL_AUTH_KEY, JSON.stringify(user));else sessionStorage.removeItem(PORTAL_AUTH_KEY);
  } catch {}
}
interface PortalCard {
  id: string;
  label: string;
  title: string;
  description: string;
  icon: ComponentType<any>;
  accent: string;
  iconBg: string;
  destination: string;
  badge?: string;
  perks: string[];
  cta: string;
}
const PORTALS: PortalCard[] = [{
  id: 'customer',
  label: 'For movers',
  title: 'Customer Portal',
  description: 'Book moves, recovery, deliveries and more. Get instant quotes or open requests to bidding.',
  icon: Users,
  accent: 'from-brand-50 to-white border-brand-200 hover:border-brand-300',
  iconBg: 'orange-gradient-bg',
  destination: '/marketplace',
  perks: ['AI-driven booking in seconds', 'Live bidding with verified carriers', 'Real-time tracking & receipts'],
  cta: 'Continue as customer'
}, {
  id: 'supplier',
  label: 'For carriers',
  title: 'Supplier Portal',
  description: 'Manage fleet, accept jobs, submit bids and track earnings — all in one dashboard.',
  icon: Truck,
  accent: 'from-blue-50 to-white border-blue-200 hover:border-blue-300',
  iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
  destination: '/supplier/login',
  perks: ['See active jobs and incoming bids', 'Counter offers and negotiate live', 'Earnings, ratings, performance'],
  cta: 'Open supplier dashboard'
}, {
  id: 'mobile',
  label: 'For customers',
  title: 'Customer App',
  description: 'The premium mobile experience for customers: AI booking, live tracking, and payments.',
  icon: Smartphone,
  accent: 'from-purple-50 to-white border-purple-200 hover:border-purple-300',
  iconBg: 'bg-gradient-to-br from-purple-500 to-fuchsia-600',
  destination: '/mobile',
  perks: ['AI booking via chat', 'Live tracking & updates', 'Seamless payments & ratings'],
  cta: 'Open customer app'
}, {
  id: 'driver',
  label: 'For drivers',
  title: 'Driver App',
  description: 'Receive jobs, navigate, and communicate with customers from one dedicated app.',
  icon: Truck,
  accent: 'from-emerald-50 to-white border-emerald-200 hover:border-emerald-300',
  iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
  destination: '/driver',
  perks: ['Accept and start jobs in one tap', 'Turn-by-turn navigation', 'Chat customers via AI or WhatsApp'],
  cta: 'Open driver app'
}];
const PAIR2_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/mUyTofs1BZsLZht378cxpn/Pair2_(2).svg';
export function Pair2Mark({
  small = false
}: {
  small?: boolean;
}) {
  return <div className="inline-flex items-center" data-id="element-2804">
      <img src={PAIR2_LOGO_URL} alt="Pair2" className={`${small ? 'h-7' : 'h-10'} w-auto object-contain`} draggable={false} data-id="element-2805" />
    </div>;
}
const RTA_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/jfEZbaQ6rCNeXEEHiKSpdo/RTA-LOGO-1.png';
export function RtaMark({
  small = false
}: {
  small?: boolean;
}) {
  return <div className="inline-flex items-center" data-id="element-2806">
      <img src={RTA_LOGO_URL} alt="Roads & Transport Authority" className={`${small ? 'h-7' : 'h-10'} w-auto object-contain`} draggable={false} data-id="element-2807" />
    </div>;
}
export function PortalSelector() {
  const navigate = useNavigate();
  const [user, setUser] = useState<PortalUser | null>(() => loadPortalUser());
  useEffect(() => {
    savePortalUser(user);
  }, [user]);
  if (!user) {
    return <PortalLogin onSuccess={setUser} data-id="element-2808" />;
  }
  return <div className="min-h-screen bg-gradient-to-b from-surface-100 to-white" data-id="element-2809">
      {/* Top bar with logos */}
      <header className="relative px-4 sm:px-8 py-5 flex items-center justify-between border-b border-gray-100 bg-white/80 backdrop-blur-md" data-id="element-2810">
        <Pair2Mark data-id="element-2811" />
        <div className="flex items-center gap-4" data-id="element-2812">
          <RtaMark data-id="element-2813" />
          <button onClick={() => setUser(null)} className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-bold transition-colors" title="Sign out" data-id="element-2814">
            <LogOut className="w-3.5 h-3.5" data-id="element-2815" />
            Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-id="element-2816">
        {/* Welcome strip */}
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center justify-between mb-8 flex-wrap gap-3" data-id="element-2817">
          <p className="text-sm text-gray-500" data-id="element-2818">
            Welcome,{' '}
            <span className="font-semibold text-gray-900" data-id="element-2819">{user.name}</span>
          </p>
          <button onClick={() => setUser(null)} className="md:hidden inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-900" data-id="element-2820">
            <LogOut className="w-3.5 h-3.5" data-id="element-2821" />
            Sign out
          </button>
        </motion.div>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12 sm:mb-14" data-id="element-2822">
          <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full bg-white text-brand-700 border border-brand-100 shadow-sm" data-id="element-2823">
            <Sparkles className="w-3.5 h-3.5" data-id="element-2824" />
            <span className="text-xs font-bold uppercase tracking-wider" data-id="element-2825">
              Pair2 × RTA · Solution Showcase
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-black text-gray-900 mb-4 tracking-tight" data-id="element-2826">
            Solution Modules
            <br className="hidden sm:block" data-id="element-2827" />{' '}
            <span className="orange-gradient-text" data-id="element-2828">for Dubai Mobility</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-2829">
            A unified platform for movers, carriers, drivers and the customer
            experience — purpose-built to power the next generation of licensed
            transport in Dubai.
          </p>
        </motion.div>

        {/* Portal cards */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-id="element-2830">
          {PORTALS.map((p, i) => {
          const Icon = p.icon;
          return <motion.button key={p.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.08
          }} whileHover={{
            y: -4
          }} onClick={() => navigate(p.destination)} className={`group text-left bg-gradient-to-br ${p.accent} border-2 rounded-3xl p-5 sm:p-6 transition-all hover:shadow-xl relative overflow-hidden flex flex-col`} data-id="element-2831">
                {p.badge && <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gray-900 text-white" data-id="element-2832">
                    <TrendingUp className="w-3 h-3" data-id="element-2833" />
                    {p.badge}
                  </span>}
                <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full bg-white/40 blur-3xl pointer-events-none" data-id="element-2834" />
                <div className="relative flex-1 flex flex-col" data-id="element-2835">
                  <div className={`w-12 h-12 rounded-2xl ${p.iconBg} flex items-center justify-center shadow-lg mb-4`} data-id="element-2836">
                    <Icon className="w-6 h-6 text-white" data-id="element-2837" />
                  </div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1" data-id="element-2838">
                    {p.label}
                  </p>
                  <h3 className="text-xl font-display font-black text-gray-900 mb-2" data-id="element-2839">
                    {p.title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed mb-4 flex-1" data-id="element-2840">
                    {p.description}
                  </p>
                  <ul className="space-y-1.5 mb-5" data-id="element-2841">
                    {p.perks.map(perk => <li key={perk} className="text-[11px] text-gray-700 flex items-start gap-2" data-id="element-2842">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" data-id="element-2843" />
                        <span data-id="element-2844">{perk}</span>
                      </li>)}
                  </ul>
                  <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-auto" data-id="element-2845">
                    <span className="text-xs font-bold text-gray-900" data-id="element-2846">
                      {p.cta}
                    </span>
                    <span className="w-9 h-9 rounded-full bg-gray-900 group-hover:bg-black text-white flex items-center justify-center transition-colors group-hover:translate-x-1" data-id="element-2847">
                      <ArrowRight className="w-4 h-4" data-id="element-2848" />
                    </span>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-12" data-id="element-2849">
          Confidential — Prepared for Roads &amp; Transport Authority, Dubai ·
          Pair2 © {new Date().getFullYear()}
        </p>
      </div>
    </div>;
}
function PortalLogin({
  onSuccess
}: {
  onSuccess: (user: PortalUser) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    setTimeout(() => {
      const key = email.trim().toLowerCase();
      const match = ALLOWED_USERS[key];
      if (!match) {
        setError("We don't recognise that email.");
        setSubmitting(false);
        return;
      }
      if (match.password !== password) {
        setError('Incorrect password.');
        setSubmitting(false);
        return;
      }
      onSuccess({
        email: key,
        name: match.name
      });
    }, 350);
  };
  return <div className="min-h-screen bg-gradient-to-b from-surface-100 to-white flex flex-col" data-id="element-2850">
      {/* Top bar with logo */}
      <header className="relative px-4 sm:px-8 py-5 flex items-center justify-between border-b border-gray-100 bg-white/80 backdrop-blur-md" data-id="element-2851">
        <Pair2Mark data-id="element-2852" />
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12" data-id="element-2853">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="relative w-full max-w-md" data-id="element-2854">
          {/* Soft glow */}
          <div className="pointer-events-none absolute -top-20 -left-20 w-72 h-72 bg-brand-200/40 rounded-full blur-3xl" data-id="element-2855" />
          <div className="pointer-events-none absolute -bottom-20 -right-20 w-72 h-72 bg-amber-200/40 rounded-full blur-3xl" data-id="element-2856" />

          <div className="relative bg-white border border-gray-100 shadow-xl rounded-3xl p-7 sm:p-8" data-id="element-2857">
            <div className="inline-flex items-center gap-1.5 mb-5 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-100" data-id="element-2858">
              <ShieldCheck className="w-3 h-3" data-id="element-2859" />
              <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-2860">
                Confidential preview
              </span>
            </div>

            <h1 className="text-3xl font-display font-black text-gray-900 tracking-tight" data-id="element-2861">
              Sign in to <span className="orange-gradient-text" data-id="element-2862">Pair2</span>
            </h1>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed" data-id="element-2863">
              Sign in to access the platform modules. This workspace is
              confidential — for authorised users only.
            </p>

            <form onSubmit={handleSubmit} className="mt-7 space-y-3" data-id="element-2864">
              <div data-id="element-2865">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 block" data-id="element-2866">
                  Email
                </label>
                <div className="relative" data-id="element-2867">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2868" />
                  <input type="email" autoComplete="email" placeholder="you@company.ae" value={email} onChange={e => setEmail(e.target.value)} required className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-3 py-3 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-brand-400 transition-colors" data-id="element-2869" />
                </div>
              </div>
              <div data-id="element-2870">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 block" data-id="element-2871">
                  Password
                </label>
                <div className="relative" data-id="element-2872">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2873" />
                  <input type={show ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-10 py-3 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-brand-400 transition-colors" data-id="element-2874" />
                  <button type="button" onClick={() => setShow(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" aria-label={show ? 'Hide password' : 'Show password'} data-id="element-2875">
                    {show ? <EyeOff className="w-4 h-4" data-id="element-2876" /> : <Eye className="w-4 h-4" data-id="element-2877" />}
                  </button>
                </div>
              </div>

              <AnimatePresence data-id="element-2878">
                {error && <motion.div initial={{
                opacity: 0,
                y: -4,
                height: 0
              }} animate={{
                opacity: 1,
                y: 0,
                height: 'auto'
              }} exit={{
                opacity: 0,
                y: -4,
                height: 0
              }} className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-id="element-2879">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" data-id="element-2880" />
                    <span data-id="element-2881">{error}</span>
                  </motion.div>}
              </AnimatePresence>

              <button type="submit" disabled={submitting} className="w-full mt-2 py-3.5 rounded-2xl orange-gradient-bg text-white font-bold text-sm shadow-[0_12px_36px_-12px_rgba(255,107,53,0.55)] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-id="element-2882">
                {submitting ? <span data-id="element-2883">Signing in…</span> : <>
                    Sign in
                    <ArrowRight className="w-4 h-4" data-id="element-2884" />
                  </>}
              </button>
            </form>

            <p className="text-[10px] text-gray-400 mt-5 text-center leading-snug" data-id="element-2885">
              Authorised personnel only · This portal is part of a confidential
              demo prepared for RTA Dubai.
            </p>
          </div>
        </motion.div>
      </div>

      <footer className="px-4 sm:px-8 py-4 text-center text-[10px] text-gray-400 border-t border-gray-100" data-id="element-2886">
        Pair2 © {new Date().getFullYear()} · Prepared for Roads &amp; Transport
        Authority, Dubai
      </footer>
    </div>;
}